<template>
  <li :data-product-id="product.product_id" class="product-card-list-item">
    <div class="product-card-flipper" :class="{ 'is-flipped': noteVisible }">
      <ProductNoteCard
        v-if="hasNote"
        class="note-card-overlay product-card-back"
        :product="userListProduct"
        :list="list"
        @close="hideNote"
      />

      <div
        class="narrative-card product-card-front"
        :class="{ 'narrative-card-set': product.is_sampler }"
      >
        <div v-if="onPPTOPage && showIcon" class="product-header">
          <div class="category-spacer" />
          <div class="category-label">
            <span class="icon-shipping"></span>
            Available {{ firstShipDate }}
          </div>
        </div>
        <div v-else class="product-header">
          <div v-if="showCategoryLabel" class="category-spacer" />
          <div v-if="showCategoryLabel" class="category-label">
            {{ categoryLabelText }}
          </div>
        </div>
        <NarrativeProductCardDisplay
          :product="product"
          :extra-savings="extraSavingsHTML"
          :pdp-link="pdpLink"
          @detail-clicked="$emit('detail-clicked', product)"
        />
        <div
          v-if="product.is_sampler && product.num_bottles_in_sampler > 1"
          class="product-set"
        >
          {{ product.num_bottles_in_sampler }}-Bottle Set
        </div>
        <div
          :ref="`${product.product_id}-product-footer`"
          class="product-footer"
        >
          <nuxt-link v-if="pdpLink" :to="product.url">
            <span class="name">{{ product.title }}</span>
            <span v-if="!available" class="sold-out"> (Sold&nbsp;Out)</span>
          </nuxt-link>
          <a v-else href="#" @click.prevent="$emit('detail-clicked', product)">
            <span class="name">{{ product.title }}</span>
            <span v-if="!available" class="sold-out"> (Sold&nbsp;Out)</span>
          </a>

          <RetailPrice
            v-if="product.show_retail_price && available"
            :show-label="false"
            :amt="Number(product.retail_price)"
          />
          <div v-if="available" class="pricing">
            <div class="price">
              <Price :amt="Number(adjustedPrice)" />
            </div>
            <TieredDiscountList
              v-show="seeAllPrices"
              :ref="`${product.product_id}-tiered-discounts`"
              :product="product"
              @close="hideAllPrices"
            />
            <div v-show="showTieredDiscount" class="count">
              {{ adjustedBottles }}
            </div>
            <button
              v-show="showTieredDiscount && !seeAllPrices"
              class="see-all"
              @click="showAllPrices"
            >
              See all prices
            </button>
          </div>
        </div>
        <div
          v-if="showErrors"
          class="product-footer pricing-detail error-box"
          @click="showErrors_ = false"
        >
          <button class="close">Close</button>
          <div>
            <ErrorList :errors="errors" />
          </div>
        </div>
        <div v-if="allowAddToCart && available" class="add-to-cart-options">
          <div class="quantity-group">
            <button
              class="quantity-change decrease"
              :disabled="!available"
              @click="incrementQuantity(-1)"
            >
              <span class="show-for-sr">Decrease</span>
            </button>
            <QuantityWidget
              :id="product.title"
              :quantity="adjustedQuantity"
              :quantity-limit="quantityLimit"
              type="text"
              :available="available"
              @clear-errors="clearErrors()"
              @set-quantity="(qty) => (quantity = qty)"
            />
            <button
              v-if="quantityLimit === null || quantityLimit > adjustedQuantity"
              class="quantity-change increase"
              :disabled="!available"
              @click="incrementQuantity(1)"
            >
              <span class="show-for-sr">Increase</span>
            </button>
          </div>
          <div class="add-to-cart">
            <button
              v-if="available"
              :data-product-id="product.product_id"
              @click="addToCart"
            >
              {{ addButtonText }}
            </button>
            <button v-else disabled>Sold&nbsp;Out</button>
          </div>
          <div
            v-if="
              showHeartIcon && !product.is_sampler && !product.is_ninja_instance
            "
            class="heart-icon"
          >
            <HeartIcon :product="product" />
          </div>
        </div>
        <div v-if="!available" class="add-to-cart-options">
          <IconButton
            class="icon-button-notify-me"
            :image="notifyMe ? filledBellIcon : bellIcon"
            :text="notifyMe ? 'Saved' : 'Notify Me'"
            alt-text="Toggle Notify Me"
            @click="$emit('notify-me-clicked', product)"
          />
          <div
            v-if="
              showHeartIcon && !product.is_sampler && !product.is_ninja_instance
            "
            class="heart-icon"
          >
            <HeartIcon :product="product" />
          </div>
        </div>

        <div v-if="list" class="note-bar-area">
          <button
            v-if="hasNote"
            class="read-note-button"
            @click.prevent="showNote"
          >
            Read Note
          </button>
          <div v-else class="empty-note-bar" />
        </div>
      </div>
    </div>
  </li>
</template>

<script>
import { requestedNotification } from '#common/utils/catalog'
import SolidBellIcon from '~/assets/images/wa-icon-bell-solid.svg'
import BellIcon from '~/assets/images/wa-icon-bell.svg'
import { productCardMixin } from '~/mixins/productCardMixin'

export default {
  name: 'NarrativeProductCard',
  components: {
    ProductNoteCard: defineAsyncComponent(
      () => import('@/components/UserList/ProductNoteCard.vue'),
    ),
    ErrorList: defineAsyncComponent(
      () => import('#common/components/ErrorList.vue'),
    ),
    Price: defineAsyncComponent(() => import('#common/components/Price.vue')),
    QuantityWidget: defineAsyncComponent(
      () => import('#wa/components/Product/QuantityWidget'),
    ),
    NarrativeProductCardDisplay: defineAsyncComponent(
      () =>
        import('#wa/components/Product/Card/NarrativeProductCardDisplay.vue'),
    ),
    TieredDiscountList: defineAsyncComponent(
      () => import('#wa/components/Product/Card/TieredDiscountList.vue'),
    ),
    RetailPrice: defineAsyncComponent(
      () => import('#wa/components/Product/Card/RetailPrice.vue'),
    ),
    HeartIcon: defineAsyncComponent(
      () => import('@/components/UserList/HeartIcon.vue'),
    ),
    IconButton: defineAsyncComponent(
      () => import('@/components/IconButton.vue'),
    ),
  },
  mixins: [productCardMixin],
  props: {
    product: {
      type: Object,
      required: true,
    },
    list: {
      type: Object,
      default: null,
    },
    partnerId: {
      type: Number,
      default: null,
    },
    allowAddToCart: {
      type: Boolean,
      default: false,
    },
    allowCategoryLabel: {
      type: Boolean,
      default: false,
    },
    pdpLink: {
      type: Boolean,
      default: true,
    },
    quantityLimit: {
      type: Number,
      default: null,
    },
    addButtonText: {
      type: String,
      default: 'Add to Cart',
    },
    allowDeeperDiscount: {
      type: Boolean,
      default: false,
    },
    extraAddToCartData: {
      type: Object,
      default: () => {
        return {}
      },
    },
    showHeartIcon: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['detail-clicked', 'notify-me-clicked'],

  data() {
    return {
      noteVisible: false,
      seeAllPrices: false,
      showErrors_: true,
      errors: [],
      quantityFocus: false,
      quantity: 1,
    }
  },
  computed: {
    userListProduct() {
      if (this.list && this.list.products) {
        return this.list.products.find(
          (x) => x.product.id == this.product.product_id,
        )
      }
      return null
    },
    hasNote() {
      return !!this.userListProduct?.note
    },
    notifyMe() {
      return requestedNotification(this.$auth.user, this.product.product_id)
    },
    bellIcon() {
      return BellIcon
    },
    filledBellIcon() {
      return SolidBellIcon
    },
  },
  methods: {
    showNote() {
      this.noteVisible = true
    },
    hideNote() {
      this.noteVisible = false
    },
  },
}
</script>
